import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { useReduxDispatch } from '../../app/store';
import { FeatureFlag, getFlagValue, setFlagValue } from '../../utils/common/feature-flag-helpers';
import { selectIsFeatureFlagsModalOpen, toggleFeatureFlagsModal } from './featureFlagSlice';

type FormData = yup.InferType<typeof validationSchema>;

const validationSchema = yup.object({
  EnableEVSELogs: yup.boolean(),
  IncreaseEVSEEnrolTimeout: yup.boolean(),
  EnableVPP: yup.boolean(),
});

const getFormOptions = () => ({
  defaultValues: getInitialFormValues(),
  resolver: yupResolver(validationSchema),
});

export function FeatureFlagsModal() {
  const { onClose } = useDisclosure();
  const dispatch = useReduxDispatch();
  const isFeatureFlagsModalOpen = useSelector(selectIsFeatureFlagsModalOpen);

  function handleClose() {
    dispatch(toggleFeatureFlagsModal(false));
    onClose();
  }

  function handleSubmitFeatureFlagsForm(formData: FormData) {
    // Set each value in local storage when submitted
    Object.entries(formData).forEach(([fieldName, value]) => {
      setFlagValue(fieldName as FeatureFlag, value as boolean);
    });

    handleClose();

    // required for theme to update
    window.location.reload();
  }

  return (
    <Modal size={'xl'} isCentered onClose={handleClose} isOpen={isFeatureFlagsModalOpen} motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader>{t('Feature Flag.title')}</ModalHeader>
        <ModalCloseButton data-testid="close-feature-flags-modal" />

        <ModalBody data-testid="feature-flags-modal">
          <Alert status="info">
            <AlertIcon />
            <Flex>
              <Box as={'span'}>{t('Feature Flag.alert message')}</Box>
            </Flex>
          </Alert>

          <FeatureFlagsForm onSubmit={handleSubmitFeatureFlagsForm} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function getInitialFormValues(): FormData {
  return {
    [FeatureFlag.EnableEVSELogs]: getFlagValue(FeatureFlag.EnableEVSELogs),
    [FeatureFlag.IncreaseEVSEEnrolTimeout]: getFlagValue(FeatureFlag.IncreaseEVSEEnrolTimeout),
    [FeatureFlag.EnableVPP]: getFlagValue(FeatureFlag.EnableVPP),
  };
}

interface FormProps {
  onSubmit: (values: FormData) => void;
}

function FeatureFlagsForm({ onSubmit }: FormProps) {
  const formOptions = getFormOptions();
  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
  } = useForm<FormData>(formOptions);
  const fieldLabels = {
    [FeatureFlag.EnableEVSELogs]: t('Feature Flag.enable evse logs'),
    [FeatureFlag.IncreaseEVSEEnrolTimeout]: t('Feature Flag.increase evse enrol timeout'),
    [FeatureFlag.EnableVPP]: t('Feature Flag.enable vpp'),
  };

  return (
    <Flex
      direction={'column'}
      justify={'center'}
      align={'center'}
      width={'100%'}
      as={'form'}
      mb={2}
      mt={4}
      onSubmit={handleSubmit(onSubmit)}
    >
      {Object.entries(FeatureFlag).map(([_, enumValue], i) => (
        <FormControl isInvalid={!!errors?.[enumValue as FeatureFlag]} key={`feature-flag-field-${i}`}>
          <Checkbox {...register(enumValue as FeatureFlag)} id={enumValue} data-testid={`${enumValue}-checkbox`}>
            {fieldLabels[enumValue]}
          </Checkbox>
        </FormControl>
      ))}

      <Button
        isDisabled={!isDirty}
        alignSelf={'start'}
        type={'submit'}
        colorScheme="primaryBranding"
        mt={3}
        data-testid="save-feature-flags-btn"
      >
        {t('Common.save')}
      </Button>
    </Flex>
  );
}
